import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  FaUser,
  FaCalendarAlt,
  FaTag,
  FaVideo,
  FaImage,
  FaFileAlt,
  FaMusic,
  FaNewspaper,
  FaChartBar,
  FaBullhorn,
  FaPenAlt,
  FaEye,
  FaHeart,
  FaThumbsDown,
  FaShareAlt,
  FaSearch,
  FaStar,
  FaPlayCircle,
  FaHeadphones,
  FaDownload,
  FaExternalLinkAlt,
  FaBookOpen,
  FaExclamationCircle,
  FaTimes,
  FaPlay,
  FaClock,
  FaArrowLeft,
  FaFilter,
  FaChevronDown,
  FaLayerGroup,
  FaFire,
  FaTrendingUp,
  FaRegClock,
  FaEllipsisV,
  FaPlus,
  FaMinus,
  FaExpand,
  FaCompress,
  FaThLarge,
  FaList,
  FaBorderAll,
  FaSquare,
  FaAngleDoubleLeft,
  FaMosque,
  FaPray,
  FaQuran,
  FaChevronRight,
  FaChevronLeft,
  FaRedoAlt,
  FaSlidersH,
  FaTh,
  FaBars,
  FaColumns,
  FaShareSquare
} from 'react-icons/fa';

const Home = () => {
  const [content, setContent] = useState([]);
  const [contentWithTokens, setContentWithTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContent, setFilteredContent] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'compact', 'masonry'
  const [featuredContent, setFeaturedContent] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '' });

  // Theme colors (keeping the same)
  const theme = {
    darkEmerald: '#255853',
    emerald: '#357E77',
    goldenSand: '#D3BA80',
    lightGolden: '#E0CFA6',
    charcoal: '#32373B',
    darkGray: '#232729',
    pureWhite: '#FFFFFF',
    lightGray: '#F0F0F0',
    border: '#E0CFA6',
    hover: 'rgba(37, 88, 83, 0.1)'
  };

  // Show toast message
  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => {
      setToast({ show: false, message: '' });
    }, 3000);
  };

  // Share function
  const handleShare = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/content/${item.content_token || item.id}`;
    const shareTitle = item.title || item.title_ar || 'KSA Files Content';
    const shareText = item.description || item.description_ar || 'Check out this content on KSA Files';
    
    if (navigator.share) {
      navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      }).catch(err => {
        console.error('Error sharing:', err);
        copyToClipboard(shareUrl);
      });
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showToast('Link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        showToast('Failed to copy link');
      });
  };

  // Filter content based on search and category
  useEffect(() => {
    let filtered = contentWithTokens;
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => 
        item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Apply sorting
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    
    setFilteredContent(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  }, [searchTerm, selectedCategory, contentWithTokens, sortBy, itemsPerPage]);

  // Extract featured content
  useEffect(() => {
    if (contentWithTokens.length > 0) {
      const featured = contentWithTokens.filter(item => item.is_featured).slice(0, 3);
      setFeaturedContent(featured);
    }
  }, [contentWithTokens]);

  // Extract unique categories
  useEffect(() => {
    if (content.length > 0) {
      const uniqueCategories = ['all', ...new Set(content.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories);
    }
  }, [content]);

  // Generate Cloudinary thumbnail URL for video
  const generateCloudinaryThumbnail = (videoUrl, options = {}) => {
    if (!videoUrl) return null;
    
    const url = new URL(videoUrl);
    const pathParts = url.pathname.split('/');
    const uploadIndex = pathParts.indexOf('upload');
    
    if (uploadIndex === -1) return videoUrl;
    
    const defaultOptions = {
      width: 400,
      height: 225,
      crop: 'fill',
      quality: 'auto:good',
      format: 'jpg'
    };
    
    const thumbOptions = { ...defaultOptions, ...options };
    
    const transformations = [
      `c_${thumbOptions.crop}`,
      `w_${thumbOptions.width}`,
      `h_${thumbOptions.height}`,
      `q_${thumbOptions.quality}`,
      `f_${thumbOptions.format}`
    ].join(',');
    
    return videoUrl.replace('/upload/', `/upload/${transformations}/`);
  };

  const getThumbnailUrl = (item) => {
    if (!item.featured_media) return null;
    
    if (item.content_type === 'video' && item.featured_media.includes('cloudinary.com')) {
      return generateCloudinaryThumbnail(item.featured_media);
    }
    
    if (item.content_type === 'image') {
      return item.featured_media;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const getContentTypeIcon = (contentType) => {
    const icons = {
      'video': <FaVideo />,
      'image': <FaImage />,
      'document': <FaFileAlt />,
      'audio': <FaMusic />,
      'news': <FaNewspaper />,
      'report': <FaChartBar />,
      'statement': <FaBullhorn />,
      'article': <FaPenAlt />,
      'islam': <FaMosque />,
      'prayer': <FaPray />,
      'quran': <FaQuran />,
      'default': <FaBookOpen />
    };
    return icons[contentType] || icons.default;
  };

  const getContentTypeText = (contentType) => {
    const types = {
      'video': 'Video',
      'image': 'Image',
      'document': 'Document',
      'audio': 'Audio',
      'news': 'News',
      'report': 'Report',
      'statement': 'Statement',
      'article': 'Article',
      'islam': 'Islam',
      'prayer': 'Prayer',
      'quran': 'Quran',
      'default': 'Content'
    };
    return types[contentType] || types.default;
  };

  const getActionButtonProps = (contentType) => {
    switch (contentType) {
      case 'video':
        return { text: 'Watch', icon: <FaPlayCircle /> };
      case 'audio':
        return { text: 'Listen', icon: <FaHeadphones /> };
      case 'document':
        return { text: 'Download', icon: <FaDownload /> };
      default:
        return { text: 'View Details', icon: <FaExternalLinkAlt /> };
    }
  };

  const fetchTokensForContent = async (contentItems) => {
    try {
      const contentIds = contentItems.map(item => item.id);
      
      const { data: tokensData, error } = await supabase
        .from('content_tokens')
        .select('token, content_id')
        .in('content_id', contentIds);

      if (error) throw error;

      const tokenMap = {};
      if (tokensData) {
        tokensData.forEach(token => {
          tokenMap[token.content_id] = token.token;
        });
      }

      return contentItems.map(item => ({
        ...item,
        content_token: tokenMap[item.id] || null
      }));
    } catch (error) {
      console.error('Error fetching tokens:', error);
      return contentItems.map(item => ({
        ...item,
        content_token: null
      }));
    }
  };

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data: contentData, error } = await supabase
        .from('content')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const contentItems = contentData || [];
      setContent(contentItems);
      
      const contentWithTokens = await fetchTokensForContent(contentItems);
      setContentWithTokens(contentWithTokens);
      setFilteredContent(contentWithTokens);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();

    const subscription = supabase
      .channel('content_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'content' },
        () => {
          fetchContent();
        }
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'content' },
        () => {
          fetchContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [fetchContent]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortBy('newest');
    setCurrentPage(1);
  };

  const getFallbackIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return '🎬';
      case 'image':
        return '🖼️';
      case 'audio':
        return '🎵';
      case 'document':
        return '📄';
      case 'islam':
        return '🕌';
      case 'prayer':
        return '🤲';
      case 'quran':
        return '📖';
      default:
        return '📎';
    }
  };

  const getContentTypeGradient = (contentType) => {
    switch (contentType) {
      case 'video':
        return 'linear-gradient(145deg, #3b82f6, #2563eb)';
      case 'image':
        return 'linear-gradient(145deg, #10b981, #059669)';
      case 'audio':
        return 'linear-gradient(145deg, #8b5cf6, #7c3aed)';
      case 'document':
        return 'linear-gradient(145deg, #f59e0b, #d97706)';
      case 'news':
        return 'linear-gradient(145deg, #ef4444, #dc2626)';
      case 'report':
        return 'linear-gradient(145deg, #6366f1, #4f46e5)';
      case 'statement':
        return 'linear-gradient(145deg, #ec4899, #db2777)';
      case 'article':
        return 'linear-gradient(145deg, #14b8a6, #0d9488)';
      case 'islam':
        return 'linear-gradient(145deg, #065f46, #047857)';
      case 'prayer':
        return 'linear-gradient(145deg, #b45309, #d97706)';
      case 'quran':
        return 'linear-gradient(145deg, #7e22ce, #9333ea)';
      default:
        return `linear-gradient(145deg, ${theme.darkEmerald}, ${theme.emerald})`;
    }
  };

  // Get current page items
  const getCurrentItems = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredContent.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Pagination controls
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Render different card layouts based on viewMode
  const renderCard = (item, index) => {
    const actionProps = getActionButtonProps(item.content_type);
    const thumbnailUrl = getThumbnailUrl(item);
    const isVideo = item.content_type === 'video';
    const gradientBg = getContentTypeGradient(item.content_type);
    
    // Masonry view
    if (viewMode === 'masonry') {
      return (
        <div key={item.id} className="content-card-masonry" style={{animationDelay: `${index * 0.1}s`}}>
          <div className="card-masonry-media" style={{ background: gradientBg }}>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={item.title || item.title_ar}
                className="card-masonry-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-masonry">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            ) : (
              <div className="no-image-masonry">{getFallbackIcon(item.content_type)}</div>
            )}
            
            <div className="card-masonry-badge">
              {getContentTypeIcon(item.content_type)}
              <span>{getContentTypeText(item.content_type)}</span>
            </div>
            
            {(item.is_breaking || item.is_featured) && (
              <div className="card-masonry-status">
                {item.is_breaking && <span className="status-breaking">URGENT</span>}
                {item.is_featured && <span className="status-featured">FEATURED</span>}
              </div>
            )}
          </div>
          
          <div className="card-masonry-content">
            <h3 className="card-masonry-title">{item.title || item.title_ar}</h3>
            
            <div className="card-masonry-meta">
              <span className="meta-item">
                <FaUser size={12} />
                {item.author || item.author_ar || 'KSA Files'}
              </span>
              <span className="meta-item">
                <FaRegClock size={12} />
                {formatDate(item.created_at)}
              </span>
            </div>
            
            {item.description && (
              <p className="card-masonry-description">
                {item.description || item.description_ar}
              </p>
            )}
            
            <div className="card-masonry-footer">
              
              <div className="card-masonry-actions">
                <button
                  onClick={(e) => handleShare(e, item)}
                  className="card-masonry-share"
                  title="Share"
                >
                  <FaShareAlt />
                </button>
                <Link 
                  to={`/content/${item.content_token || item.id}`}
                  className="card-masonry-button"
                >
                  {actionProps.icon}
                  {actionProps.text}
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // List view
    if (viewMode === 'list') {
      return (
        <div key={item.id} className="content-card-list" style={{animationDelay: `${index * 0.05}s`}}>
          <div className="card-list-image" style={{ background: gradientBg }}>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={item.title || item.title_ar}
                className="card-list-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-list">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            ) : (
              <div className="no-image-list">{getFallbackIcon(item.content_type)}</div>
            )}
            {(isVideo || item.content_type === 'audio') && (
              <div className="list-play-icon">
                {isVideo ? <FaPlay size={16} /> : <FaHeadphones size={16} />}
              </div>
            )}
          </div>
          
          <div className="card-list-content">
            <div className="card-list-header">
              <div className="card-list-type">
                {getContentTypeIcon(item.content_type)}
                <span>{getContentTypeText(item.content_type)}</span>
              </div>
              <div className="card-list-badges-horizontal">
                {item.is_breaking && <span className="badge-horizontal breaking">Urgent</span>}
                {item.is_featured && <span className="badge-horizontal featured">Featured</span>}
              </div>
            </div>
            
            <h3 className="card-list-title">{item.title || item.title_ar}</h3>
            
            {item.description && (
              <p className="card-list-description">
                {item.description || item.description_ar}
              </p>
            )}
            
            <div className="card-list-meta">
              <span className="card-list-meta-item">
                <FaUser />
                {item.author || item.author_ar || 'KSA Files'}
              </span>
              <span className="card-list-meta-item">
                <FaRegClock />
                {formatDate(item.created_at)}
              </span>
              <span className="card-list-meta-item">
                <FaTag />
                {item.category || 'General'}
              </span>
            </div>
            
            <div className="card-list-footer">
            
              
              <div className="card-list-actions">
                <button
                  onClick={(e) => handleShare(e, item)}
                  className="card-list-share"
                  title="Share"
                >
                  <FaShareAlt />
                </button>
                <Link 
                  to={`/content/${item.content_token || item.id}`}
                  className="card-list-button"
                >
                  {actionProps.icon}
                  {actionProps.text}
                  <FaAngleDoubleLeft />
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Compact view
    if (viewMode === 'compact') {
      return (
        <div key={item.id} className="content-card-compact" style={{animationDelay: `${index * 0.05}s`}}>
          <div className="compact-image" style={{ background: gradientBg }}>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={item.title || item.title_ar}
                className="compact-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-compact">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            ) : (
              <div className="no-image-compact">{getFallbackIcon(item.content_type)}</div>
            )}
          </div>
          
          <div className="compact-content">
            <div className="compact-header">
              <h3 className="compact-title">{item.title || item.title_ar}</h3>
              <div className="compact-type">
                {getContentTypeIcon(item.content_type)}
              </div>
            </div>
            
            <div className="compact-meta">
              <span><FaRegClock size={10} /> {formatDate(item.created_at)}</span>
              <span className="compact-category">{item.category || 'General'}</span>
            </div>
            
            <div className="compact-footer">
           
              
              <div className="compact-actions">
                <button
                  onClick={(e) => handleShare(e, item)}
                  className="compact-share"
                  title="Share"
                >
                  <FaShareAlt size={12} />
                </button>
                <Link 
                  to={`/content/${item.content_token || item.id}`}
                  className="compact-button"
                >
                  <FaArrowLeft />
                </Link>
              </div>
            </div>
            
            {(item.is_breaking || item.is_featured) && (
              <div className="compact-badge">
                {item.is_breaking && <span className="badge-compact breaking">!</span>}
                {item.is_featured && <span className="badge-compact featured">★</span>}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Default grid view - Modern redesign
    return (
      <div key={item.id} className="content-card-modern" style={{animationDelay: `${index * 0.1}s`}}>
        <div className="card-modern-media" style={{ background: gradientBg }}>
          {thumbnailUrl ? (
            <>
              <img 
                src={thumbnailUrl} 
                alt={item.title || item.title_ar}
                className="card-modern-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-modern">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            </>
          ) : (
            <div className="no-image-modern">{getFallbackIcon(item.content_type)}</div>
          )}
          
          <div className="card-modern-badge">
            {getContentTypeIcon(item.content_type)}
            <span>{getContentTypeText(item.content_type)}</span>
          </div>
          
          {(isVideo || item.content_type === 'audio') && (
            <div className="card-modern-play">
              {isVideo ? <FaPlay size={20} /> : <FaHeadphones size={20} />}
            </div>
          )}
        </div>
        
        <div className="card-modern-content">
          <div className="card-modern-category">
            <FaTag size={12} />
            <span>{item.category || 'General'}</span>
          </div>
          
          <h3 className="card-modern-title">{item.title || item.title_ar}</h3>
          
          <div className="card-modern-author">
            <FaUser size={12} />
            <span>{item.author || item.author_ar || 'KSA Files'}</span>
            <span className="card-modern-date">
              <FaRegClock size={12} />
              {formatDate(item.created_at)}
            </span>
          </div>
          
         
          
          <div className="card-modern-actions">
            <Link 
              to={`/content/${item.content_token || item.id}`}
              className="card-modern-button"
            >
              <span>{actionProps.text}</span>
              {actionProps.icon}
            </Link>
            
            <button
              onClick={(e) => handleShare(e, item)}
              className="card-modern-share"
              title="Share"
            >
              <FaShareAlt />
            </button>
            
            {(item.is_breaking || item.is_featured) && (
              <div className="card-modern-badges">
                {item.is_breaking && (
                  <div className="badge-modern breaking" title="Urgent">
                    <FaExclamationCircle />
                  </div>
                )}
                {item.is_featured && (
                  <div className="badge-modern featured" title="Featured">
                    <FaStar />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home-modern">
      <style>{`
        .home-modern {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'ITF Qomra Arabic', 'Segoe UI', sans-serif;
          direction: ltr;
          position: relative;
        }

        /* Toast Notification */
        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${theme.darkEmerald};
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 9999;
          animation: slideIn 0.3s ease;
          font-weight: 500;
          font-size: clamp(0.875rem, 2vw, 1rem);
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Modern Header */
        .home-header-modern {
          background: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          padding: 0.75rem 1rem;
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
          border-bottom: 1px solid rgba(37, 88, 83, 0.1);
        }

        @media (min-width: 640px) {
          .home-header-modern {
            padding: 0.75rem 1.5rem;
          }
        }

        @media (min-width: 1024px) {
          .home-header-modern {
            padding: 1rem 2rem;
          }
        }

        .header-content-modern {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 768px) {
          .header-content-modern {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 640px) {
          .header-left {
            flex-direction: row;
            align-items: center;
            gap: 2rem;
          }
        }

        .header-logo h1 {
          font-size: clamp(1.5rem, 4vw, 1.8rem);
          font-weight: 700;
          color: ${theme.darkEmerald};
          margin: 0;
          position: relative;
        }

        .header-logo h1:after {
          content: '';
          position: absolute;
          bottom: -6px;
          left: 0;
          width: 40px;
          height: 3px;
          background: linear-gradient(90deg, ${theme.darkEmerald}, ${theme.emerald});
          border-radius: 2px;
        }

        @media (min-width: 640px) {
          .header-logo h1:after {
            width: 60px;
          }
        }

        .header-nav-modern {
          display: flex;
          gap: 0.25rem;
          align-items: center;
          flex-wrap: wrap;
        }

        @media (min-width: 480px) {
          .header-nav-modern {
            gap: 0.5rem;
          }
        }

        .nav-link-modern {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #4a5568;
          text-decoration: none;
          font-weight: 500;
          padding: 0.5rem 0.75rem;
          border-radius: 40px;
          transition: all 0.3s ease;
          font-size: clamp(0.75rem, 2vw, 0.9rem);
        }

        @media (min-width: 480px) {
          .nav-link-modern {
            padding: 0.6rem 1.2rem;
            gap: 0.5rem;
          }
        }

        .nav-link-modern:hover {
          color: ${theme.darkEmerald};
          background: ${theme.hover};
        }

        .nav-link-modern.active {
          color: ${theme.darkEmerald};
          background: ${theme.hover};
        }

        .nav-icon {
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        .header-right {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .view-toggle-modern {
          display: flex;
          gap: 0.25rem;
          background: #f1f5f9;
          padding: 0.25rem;
          border-radius: 12px;
          width: fit-content;
        }

        .view-btn-modern {
          width: clamp(32px, 8vw, 40px);
          height: clamp(32px, 8vw, 40px);
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: #64748b;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: clamp(0.9rem, 2vw, 1.1rem);
        }

        .view-btn-modern:hover {
          color: ${theme.darkEmerald};
        }

        .view-btn-modern.active {
          background: white;
          color: ${theme.darkEmerald};
          box-shadow: 0 2px 8px rgba(37, 88, 83, 0.1);
        }

        /* Main Container */
        .main-container-modern {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem;
        }

        @media (min-width: 640px) {
          .main-container-modern {
            padding: 1.5rem;
          }
        }

        @media (min-width: 1024px) {
          .main-container-modern {
            padding: 2rem;
          }
        }

        /* Featured Section */
        .featured-section {
          margin-bottom: clamp(1.5rem, 4vw, 3rem);
        }

        .featured-title {
          font-size: clamp(1.2rem, 3vw, 1.5rem);
          font-weight: 700;
          color: ${theme.darkEmerald};
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .featured-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        @media (min-width: 640px) {
          .featured-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
        }

        @media (min-width: 1024px) {
          .featured-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.5rem;
          }
        }

        .featured-card {
          background: white;
          border-radius: clamp(12px, 2vw, 20px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          transition: all 0.3s ease;
          border: 1px solid rgba(37, 88, 83, 0.1);
          text-decoration: none;
          color: inherit;
        }

        .featured-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 30px rgba(37, 88, 83, 0.1);
          border-color: ${theme.darkEmerald};
        }

        .featured-media {
          height: clamp(140px, 30vw, 180px);
          position: relative;
          overflow: hidden;
        }

        .featured-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .featured-card:hover .featured-media img {
          transform: scale(1.05);
        }

        .featured-badge {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          background: rgba(255, 255, 255, 0.95);
          color: ${theme.darkEmerald};
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
          font-size: clamp(0.6rem, 1.5vw, 0.75rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        @media (min-width: 640px) {
          .featured-badge {
            top: 1rem;
            left: 1rem;
            padding: 0.25rem 0.75rem;
          }
        }

        .featured-content {
          padding: clamp(0.75rem, 2vw, 1.5rem);
        }

        .featured-content h3 {
          font-size: clamp(0.9rem, 2.5vw, 1.1rem);
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .featured-meta {
          display: flex;
          align-items: center;
          gap: clamp(0.5rem, 2vw, 1rem);
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          color: #64748b;
          flex-wrap: wrap;
        }

        /* Filters Section Modern */
        .filters-section-modern {
          background: white;
          border-radius: clamp(12px, 2vw, 16px);
          padding: clamp(0.75rem, 2vw, 1.5rem);
          margin-bottom: clamp(1rem, 2vw, 2rem);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(37, 88, 83, 0.1);
        }

        .filters-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          margin-bottom: ${showFilters ? '1rem' : '0'};
        }

        @media (min-width: 768px) {
          .filters-header-modern {
            margin-bottom: ${showFilters ? '1.5rem' : '0'};
          }
        }

        .filters-title-modern {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: ${theme.darkEmerald};
          font-weight: 600;
          font-size: clamp(0.9rem, 2vw, 1rem);
        }

        .filters-toggle-modern {
          color: ${theme.darkEmerald};
          transition: transform 0.3s ease;
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        .filters-toggle-modern.open {
          transform: rotate(180deg);
        }

        .filters-grid-modern {
          display: ${showFilters ? 'grid' : 'none'};
          grid-template-columns: 1fr;
          gap: 0.75rem;
          align-items: center;
        }

        @media (min-width: 640px) {
          .filters-grid-modern {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
        }

        @media (min-width: 1024px) {
          .filters-grid-modern {
            grid-template-columns: 2fr 1fr auto;
          }
        }

        .search-wrapper-modern {
          position: relative;
        }

        .search-icon-modern {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: ${theme.darkEmerald};
          opacity: 0.5;
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        @media (min-width: 640px) {
          .search-icon-modern {
            left: 15px;
          }
        }

        .search-input-modern {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2rem;
          border: 2px solid #e2e8f0;
          border-radius: clamp(8px, 2vw, 12px);
          font-size: clamp(0.85rem, 2vw, 0.95rem);
          background: white;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        @media (min-width: 640px) {
          .search-input-modern {
            padding: 1rem 1.25rem 1rem 2.5rem;
          }
        }

        .search-input-modern:focus {
          outline: none;
          border-color: ${theme.darkEmerald};
          box-shadow: 0 0 0 4px ${theme.hover};
        }

        .filter-group-modern {
          position: relative;
        }

        .filter-select-modern {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: clamp(8px, 2vw, 12px);
          font-size: clamp(0.85rem, 2vw, 0.95rem);
          background: white;
          color: #4a5568;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          appearance: none;
        }

        @media (min-width: 640px) {
          .filter-select-modern {
            padding: 1rem 1.25rem;
          }
        }

        .filter-select-modern:focus {
          outline: none;
          border-color: ${theme.darkEmerald};
        }

        .select-arrow-modern {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: ${theme.darkEmerald};
          pointer-events: none;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
        }

        @media (min-width: 640px) {
          .select-arrow-modern {
            right: 15px;
          }
        }

        .clear-filters-modern {
          padding: 0.75rem 1rem;
          background: #f1f5f9;
          color: #4a5568;
          border: none;
          border-radius: clamp(8px, 2vw, 12px);
          font-size: clamp(0.85rem, 2vw, 0.95rem);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-family: inherit;
          white-space: nowrap;
        }

        @media (min-width: 640px) {
          .clear-filters-modern {
            padding: 1rem 1.5rem;
          }
        }

        .clear-filters-modern:hover {
          background: #e2e8f0;
          color: ${theme.darkEmerald};
        }

        /* Results Bar Modern */
        .results-bar-modern {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        @media (min-width: 640px) {
          .results-bar-modern {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          }
        }

        .results-count-modern {
          color: ${theme.darkEmerald};
          font-size: clamp(0.85rem, 2vw, 0.95rem);
          font-weight: 500;
          background: ${theme.hover};
          padding: 0.5rem 1rem;
          border-radius: 40px;
          width: fit-content;
        }

        @media (min-width: 640px) {
          .results-count-modern {
            padding: 0.5rem 1.25rem;
          }
        }

        .sort-wrapper-modern {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .sort-label-modern {
          color: #4a5568;
          font-size: clamp(0.8rem, 2vw, 0.9rem);
        }

        .sort-select-modern {
          padding: 0.5rem 2rem 0.5rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 40px;
          font-size: clamp(0.8rem, 2vw, 0.9rem);
          background: white;
          color: #4a5568;
          cursor: pointer;
          font-family: inherit;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23255853' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          background-size: 12px;
        }

        /* Modern Grid View */
        .content-grid-modern {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          animation: fadeIn 0.5s ease;
        }

        @media (min-width: 480px) {
          .content-grid-modern {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
        }

        @media (min-width: 768px) {
          .content-grid-modern {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
        }

        @media (min-width: 1024px) {
          .content-grid-modern {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.5rem;
          }
        }

        @media (min-width: 1280px) {
          .content-grid-modern {
            grid-template-columns: repeat(4, 1fr);
            gap: 2rem;
          }
        }

        .content-card-modern {
          background: white;
          border-radius: clamp(12px, 2vw, 20px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(37, 88, 83, 0.1);
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
          display: flex;
          flex-direction: column;
        }

        .content-card-modern:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 30px rgba(37, 88, 83, 0.1);
          border-color: ${theme.darkEmerald};
        }

        .card-modern-media {
          position: relative;
          height: clamp(140px, 25vw, 180px);
          overflow: hidden;
        }

        .card-modern-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .content-card-modern:hover .card-modern-image {
          transform: scale(1.05);
        }

        .card-modern-badge {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          background: rgba(255, 255, 255, 0.95);
          color: ${theme.darkEmerald};
          padding: 0.25rem 0.6rem;
          border-radius: 40px;
          font-size: clamp(0.6rem, 1.5vw, 0.75rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        @media (min-width: 768px) {
          .card-modern-badge {
            top: 1rem;
            left: 1rem;
            padding: 0.4rem 0.8rem;
          }
        }

        .card-modern-play {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: clamp(36px, 8vw, 48px);
          height: clamp(36px, 8vw, 48px);
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme.darkEmerald};
          font-size: clamp(0.9rem, 2vw, 1.2rem);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .content-card-modern:hover .card-modern-play {
          opacity: 1;
        }

        .no-image-modern {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: clamp(1.5rem, 4vw, 2rem);
        }

        .card-modern-content {
          padding: clamp(0.75rem, 2vw, 1.5rem);
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .card-modern-category {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 0.2rem 0.6rem;
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border-radius: 40px;
          font-size: clamp(0.6rem, 1.5vw, 0.7rem);
          font-weight: 600;
          margin-bottom: 0.5rem;
          width: fit-content;
        }

        @media (min-width: 768px) {
          .card-modern-category {
            padding: 0.25rem 0.75rem;
            margin-bottom: 0.75rem;
          }
        }

        .card-modern-title {
          font-size: clamp(0.9rem, 2.5vw, 1.1rem);
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }

        .card-modern-author {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          margin-bottom: 0.5rem;
          flex-wrap: wrap;
        }

        .card-modern-date {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-left: 0;
        }

        @media (min-width: 480px) {
          .card-modern-date {
            margin-left: auto;
          }
        }

        .card-modern-stats {
          display: flex;
          gap: 0.75rem;
          padding: 0.5rem 0;
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 0.75rem;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          color: #64748b;
        }

        @media (min-width: 768px) {
          .card-modern-stats {
            padding: 0.75rem 0;
            margin-bottom: 1rem;
          }
        }

        .card-modern-stats span {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .card-modern-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: auto;
        }

        .card-modern-button {
          flex: 1;
          background: linear-gradient(135deg, ${theme.darkEmerald}, ${theme.emerald});
          color: white;
          border: none;
          padding: 0.5rem 0.75rem;
          border-radius: clamp(8px, 2vw, 12px);
          font-size: clamp(0.75rem, 2vw, 0.85rem);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        @media (min-width: 768px) {
          .card-modern-button {
            padding: 0.6rem 1rem;
            gap: 8px;
          }
        }

        .card-modern-button:hover {
          background: linear-gradient(135deg, #1f4a45, #2d6b64);
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(37, 88, 83, 0.2);
        }

        .card-modern-share {
          width: clamp(32px, 8vw, 40px);
          height: clamp(32px, 8vw, 40px);
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border: none;
          border-radius: clamp(8px, 2vw, 12px);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        .card-modern-share:hover {
          background: ${theme.darkEmerald};
          color: white;
          transform: translateY(-2px);
        }

        .card-modern-badges {
          display: flex;
          gap: 0.25rem;
        }

        .badge-modern {
          width: clamp(28px, 6vw, 32px);
          height: clamp(28px, 6vw, 32px);
          border-radius: clamp(6px, 1.5vw, 8px);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          cursor: help;
        }

        .badge-modern.breaking {
          background: #dc2626;
        }

        .badge-modern.featured {
          background: #f59e0b;
        }

        /* Masonry View */
        .content-masonry {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          animation: fadeIn 0.5s ease;
        }

        @media (min-width: 480px) {
          .content-masonry {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 768px) {
          .content-masonry {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
        }

        @media (min-width: 1024px) {
          .content-masonry {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.5rem;
          }
        }

        @media (min-width: 1280px) {
          .content-masonry {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .content-card-masonry {
          background: white;
          border-radius: clamp(12px, 2vw, 16px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(37, 88, 83, 0.1);
          transition: all 0.3s ease;
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
          break-inside: avoid;
        }

        .content-card-masonry:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 30px rgba(37, 88, 83, 0.1);
          border-color: ${theme.darkEmerald};
        }

        .card-masonry-media {
          position: relative;
          height: clamp(160px, 30vw, 200px);
          overflow: hidden;
        }

        .card-masonry-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .content-card-masonry:hover .card-masonry-image {
          transform: scale(1.05);
        }

        .card-masonry-badge {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          background: rgba(255, 255, 255, 0.95);
          color: ${theme.darkEmerald};
          padding: 0.2rem 0.5rem;
          border-radius: 20px;
          font-size: clamp(0.6rem, 1.5vw, 0.7rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        @media (min-width: 768px) {
          .card-masonry-badge {
            top: 1rem;
            left: 1rem;
            padding: 0.3rem 0.6rem;
          }
        }

        .card-masonry-status {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          display: flex;
          gap: 0.25rem;
        }

        .status-breaking {
          background: #dc2626;
          color: white;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          font-size: clamp(0.55rem, 1.2vw, 0.6rem);
          font-weight: 600;
        }

        .status-featured {
          background: #f59e0b;
          color: white;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          font-size: clamp(0.55rem, 1.2vw, 0.6rem);
          font-weight: 600;
        }

        .no-image-masonry {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: clamp(1.5rem, 4vw, 2rem);
        }

        .card-masonry-content {
          padding: clamp(0.75rem, 2vw, 1.25rem);
        }

        .card-masonry-title {
          font-size: clamp(0.9rem, 2.2vw, 1rem);
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-masonry-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          font-size: clamp(0.65rem, 1.5vw, 0.75rem);
          color: #64748b;
          margin-bottom: 0.5rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.2rem;
        }

        .card-masonry-description {
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 0.75rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .card-masonry-description {
            margin-bottom: 1rem;
          }
        }

        .card-masonry-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-masonry-stats {
          display: flex;
          gap: 0.5rem;
          font-size: clamp(0.6rem, 1.5vw, 0.7rem);
          color: #64748b;
        }

        .card-masonry-stats span {
          display: flex;
          align-items: center;
          gap: 0.2rem;
        }

        .card-masonry-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .card-masonry-share {
          width: clamp(24px, 5vw, 28px);
          height: clamp(24px, 5vw, 28px);
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border: none;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
        }

        .card-masonry-share:hover {
          background: ${theme.darkEmerald};
          color: white;
        }

        .card-masonry-button {
          padding: 0.3rem 0.6rem;
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border-radius: 20px;
          text-decoration: none;
          font-size: clamp(0.6rem, 1.5vw, 0.7rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.3s ease;
        }

        @media (min-width: 768px) {
          .card-masonry-button {
            padding: 0.4rem 0.8rem;
          }
        }

        .card-masonry-button:hover {
          background: ${theme.darkEmerald};
          color: white;
        }

        /* List View */
        .content-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .content-card-list {
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: clamp(12px, 2vw, 16px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(37, 88, 83, 0.1);
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
        }

        @media (min-width: 640px) {
          .content-card-list {
            flex-direction: row;
          }
        }

        .card-list-image {
          width: 100%;
          height: 160px;
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 640px) {
          .card-list-image {
            width: 200px;
            height: 200px;
          }
        }

        @media (min-width: 1024px) {
          .card-list-image {
            width: 240px;
            height: 240px;
          }
        }

        .card-list-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .list-play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme.darkEmerald};
          font-size: 1rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .no-image-list {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 2rem;
        }

        .card-list-content {
          flex: 1;
          padding: clamp(0.75rem, 2vw, 1.5rem);
          display: flex;
          flex-direction: column;
        }

        .card-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .card-list-type {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: ${theme.darkEmerald};
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          font-weight: 600;
        }

        .card-list-badges-horizontal {
          display: flex;
          gap: 0.5rem;
        }

        .badge-horizontal {
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
          font-size: clamp(0.55rem, 1.2vw, 0.6rem);
          font-weight: 600;
          color: white;
        }

        .badge-horizontal.breaking {
          background: #dc2626;
        }

        .badge-horizontal.featured {
          background: #f59e0b;
        }

        .card-list-title {
          font-size: clamp(1rem, 2.5vw, 1.2rem);
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .card-list-description {
          font-size: clamp(0.8rem, 1.8vw, 0.9rem);
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .card-list-description {
            -webkit-line-clamp: 3;
          }
        }

        .card-list-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1rem;
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
          color: #64748b;
        }

        .card-list-meta-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .card-list-footer {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: auto;
        }

        @media (min-width: 480px) {
          .card-list-footer {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
        }

        .card-list-stats {
          display: flex;
          gap: 1rem;
        }

        .card-list-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .card-list-share {
          width: 36px;
          height: 36px;
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border: none;
          border-radius: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .card-list-share:hover {
          background: ${theme.darkEmerald};
          color: white;
        }

        .card-list-button {
          padding: 0.5rem 1.25rem;
          background: ${theme.darkEmerald};
          color: white;
          border-radius: 40px;
          text-decoration: none;
          font-size: clamp(0.75rem, 1.8vw, 0.85rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .card-list-button:hover {
          background: #1f4a45;
          transform: translateX(-2px);
        }

        /* Compact View */
        .content-compact {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        @media (min-width: 480px) {
          .content-compact {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 768px) {
          .content-compact {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .content-compact {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .content-card-compact {
          display: flex;
          background: white;
          border-radius: clamp(10px, 1.5vw, 12px);
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(37, 88, 83, 0.1);
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
        }

        .compact-image {
          width: 80px;
          height: 80px;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 480px) {
          .compact-image {
            width: 100px;
            height: 100px;
          }
        }

        .compact-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image-compact {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .compact-content {
          flex: 1;
          padding: 0.5rem;
          position: relative;
        }

        @media (min-width: 480px) {
          .compact-content {
            padding: 0.75rem;
          }
        }

        .compact-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.25rem;
        }

        .compact-title {
          font-size: clamp(0.8rem, 2vw, 0.9rem);
          font-weight: 600;
          color: #1e293b;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }

        .compact-type {
          color: ${theme.darkEmerald};
          font-size: clamp(0.7rem, 1.5vw, 0.8rem);
        }

        .compact-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: clamp(0.6rem, 1.2vw, 0.65rem);
          color: #64748b;
          margin: 0.25rem 0;
          flex-wrap: wrap;
        }

        .compact-category {
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          padding: 0.1rem 0.4rem;
          border-radius: 4px;
          font-size: clamp(0.55rem, 1.2vw, 0.6rem);
          font-weight: 600;
        }

        .compact-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.25rem;
        }

        .compact-stats {
          font-size: clamp(0.55rem, 1.2vw, 0.6rem);
          color: #64748b;
        }

        .compact-actions {
          display: flex;
          align-items: center;
          gap: 0.2rem;
        }

        .compact-share {
          width: 24px;
          height: 24px;
          background: ${theme.hover};
          color: ${theme.darkEmerald};
          border: none;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .compact-share:hover {
          background: ${theme.darkEmerald};
          color: white;
        }

        .compact-button {
          width: 24px;
          height: 24px;
          background: ${theme.hover};
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme.darkEmerald};
          transition: all 0.3s ease;
        }

        .compact-button:hover {
          background: ${theme.darkEmerald};
          color: white;
        }

        .compact-badge {
          position: absolute;
          top: 0.25rem;
          right: 0.25rem;
        }

        .badge-compact {
          display: inline-block;
          width: 18px;
          height: 18px;
          border-radius: 4px;
          color: white;
          font-size: 0.6rem;
          font-weight: 600;
          text-align: center;
          line-height: 18px;
        }

        .badge-compact.breaking {
          background: #dc2626;
        }

        .badge-compact.featured {
          background: #f59e0b;
        }

        /* Pagination */
        .pagination-modern {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.25rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        @media (min-width: 640px) {
          .pagination-modern {
            gap: 0.5rem;
            margin-top: 3rem;
          }
        }

        .pagination-btn {
          width: clamp(32px, 8vw, 40px);
          height: clamp(32px, 8vw, 40px);
          border: 2px solid #e2e8f0;
          background: white;
          color: #4a5568;
          border-radius: clamp(8px, 1.5vw, 12px);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: ${theme.darkEmerald};
          color: ${theme.darkEmerald};
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-numbers {
          display: flex;
          gap: 0.2rem;
          flex-wrap: wrap;
        }

        @media (min-width: 640px) {
          .pagination-numbers {
            gap: 0.25rem;
          }
        }

        .pagination-number {
          min-width: clamp(32px, 8vw, 40px);
          height: clamp(32px, 8vw, 40px);
          border: 2px solid #e2e8f0;
          background: white;
          color: #4a5568;
          border-radius: clamp(8px, 1.5vw, 12px);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
          font-size: clamp(0.8rem, 2vw, 1rem);
        }

        .pagination-number:hover {
          border-color: ${theme.darkEmerald};
          color: ${theme.darkEmerald};
        }

        .pagination-number.active {
          background: ${theme.darkEmerald};
          border-color: ${theme.darkEmerald};
          color: white;
        }

        /* Loading States */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 1rem;
          min-height: 300px;
        }

        @media (min-width: 768px) {
          .loading-container {
            padding: 5rem;
            min-height: 400px;
          }
        }

        .empty-state {
          text-align: center;
          padding: 2rem 1rem;
          background: white;
          border-radius: clamp(16px, 3vw, 30px);
          margin: 1rem 0;
        }

        @media (min-width: 768px) {
          .empty-state {
            padding: 4rem 2rem;
            margin: 2rem 0;
          }
        }

        .empty-icon {
          font-size: clamp(2.5rem, 8vw, 4rem);
          margin-bottom: 1rem;
          color: #cbd5e0;
        }

        @media (min-width: 768px) {
          .empty-icon {
            margin-bottom: 1.5rem;
          }
        }

        .empty-text {
          font-size: clamp(1rem, 3vw, 1.25rem);
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .empty-subtext {
          font-size: clamp(0.8rem, 2vw, 0.95rem);
          color: #a0aec0;
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-notification">
          {toast.message}
        </div>
      )}

      {/* Modern Header */}
      <header className="home-header-modern">
        <div className="header-content-modern">
          <div className="header-left">
            <div className="header-logo">
              <h1>KSA Files</h1>
            </div>
            
            <nav className="header-nav-modern">
              <Link to="/islam" className="nav-link-modern">
                <FaMosque className="nav-icon" />
                <span>Islam</span>
              </Link>
              <Link to="/videos" className="nav-link-modern">
                <FaVideo className="nav-icon" />
                <span>Videos</span>
              </Link>
              <Link to="/images" className="nav-link-modern">
                <FaImage className="nav-icon" />
                <span>Images</span>
              </Link>
            
            </nav>
          </div>
          
          <div className="header-right">
            <div className="view-toggle-modern">
              <button 
                className={`view-btn-modern ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FaThLarge />
              </button>
              <button 
                className={`view-btn-modern ${viewMode === 'masonry' ? 'active' : ''}`}
                onClick={() => setViewMode('masonry')}
                title="Masonry View"
              >
                <FaColumns />
              </button>
              <button 
                className={`view-btn-modern ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <FaList />
              </button>
              <button 
                className={`view-btn-modern ${viewMode === 'compact' ? 'active' : ''}`}
                onClick={() => setViewMode('compact')}
                title="Compact View"
              >
                <FaSquare />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-container-modern">
        {/* Featured Section */}
        {featuredContent.length > 0 && viewMode === 'grid' && (
          <div className="featured-section">
            <h2 className="featured-title">
              <FaStar style={{ color: theme.goldenSand }} />
              Featured Content
            </h2>
            <div className="featured-grid">
              {featuredContent.map((item) => {
                const thumbnailUrl = getThumbnailUrl(item);
                const gradientBg = getContentTypeGradient(item.content_type);
                
                return (
                  <Link 
                    key={item.id}
                    to={`/content/${item.content_token || item.id}`}
                    className="featured-card"
                  >
                    <div className="featured-media" style={{ background: gradientBg }}>
                      {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt={item.title} />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '2rem'
                        }}>
                          {getFallbackIcon(item.content_type)}
                        </div>
                      )}
                      <div className="featured-badge">
                        {getContentTypeIcon(item.content_type)}
                        <span>{getContentTypeText(item.content_type)}</span>
                      </div>
                    </div>
                    <div className="featured-content">
                      <h3>{item.title || item.title_ar}</h3>
                      <div className="featured-meta">
                        <span><FaUser /> {item.author || item.author_ar}</span>
                        <span><FaEye /> {item.views || 0}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="filters-section-modern">
          <div className="filters-header-modern" onClick={() => setShowFilters(!showFilters)}>
            <div className="filters-title-modern">
              <FaFilter />
              <span>Search & Filter Options</span>
            </div>
            <FaChevronDown className={`filters-toggle-modern ${showFilters ? 'open' : ''}`} />
          </div>

          <div className="filters-grid-modern">
            <div className="search-wrapper-modern">
              <FaSearch className="search-icon-modern" />
              <input
                type="text"
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-modern"
              />
            </div>
            
            <div className="filter-group-modern">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select-modern"
              >
                <option value="all">All Categories</option>
                {categories.filter(cat => cat !== 'all').map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <FaChevronDown className="select-arrow-modern" />
            </div>
            
            {(searchTerm || selectedCategory !== 'all') && (
              <button
                onClick={handleClearFilters}
                className="clear-filters-modern"
              >
                <FaTimes />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Results Bar */}
        <div className="results-bar-modern">
          <div className="results-count-modern">
            {filteredContent.length} {filteredContent.length === 1 ? 'Result' : 'Results'}
          </div>
          
          <div className="sort-wrapper-modern">
            <span className="sort-label-modern">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select-modern"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="popular">Most Viewed</option>
            </select>
          </div>
        </div>

        {/* Content Grid/List */}
        {loading ? (
          <div className="loading-container">
            <Spinner animation="border" variant="primary" />
            <span style={{marginTop: '1.5rem', color: theme.darkEmerald, fontSize: '1.1rem'}}>
              Loading...
            </span>
          </div>
        ) : filteredContent.length > 0 ? (
          <>
            {viewMode === 'grid' && (
              <div className="content-grid-modern">
                {getCurrentItems().map((item, index) => renderCard(item, index))}
              </div>
            )}
            
            {viewMode === 'masonry' && (
              <div className="content-masonry">
                {getCurrentItems().map((item, index) => renderCard(item, index))}
              </div>
            )}
            
            {viewMode === 'list' && (
              <div className="content-list">
                {getCurrentItems().map((item, index) => renderCard(item, index))}
              </div>
            )}
            
            {viewMode === 'compact' && (
              <div className="content-compact">
                {getCurrentItems().map((item, index) => renderCard(item, index))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-modern">
                <button 
                  className="pagination-btn" 
                  onClick={prevPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft />
                </button>
                
                <div className="pagination-numbers">
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => paginate(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === currentPage - 2 ||
                      pageNum === currentPage + 2
                    ) {
                      return <span key={pageNum}>...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <button 
                  className="pagination-btn" 
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaFileAlt />
            </div>
            <p className="empty-text">
              No content available
            </p>
            {(searchTerm || selectedCategory !== 'all') && (
              <p className="empty-subtext">
                Try changing your search criteria or clearing filters
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;